import { useEffect, useState } from 'react'
import { Box, CloseButton, Dialog, Grid, HStack, Portal, Spinner, Text } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { LuUser } from 'react-icons/lu'
import { fetchVideoOwner } from '../services/firebaseService'
import { videoInfoRows } from '../utils/videoInfo'
import { useTranslation } from '../i18n'

const MONO = "'JetBrains Mono', monospace"

function Field({ label, children, mono }) {
    return (
        <>
            <Text fontSize="sm" color="var(--mc-text-muted)" wordBreak="break-all">{label}</Text>
            <Text fontSize="sm" color="var(--mc-text)" wordBreak="break-all" fontFamily={mono ? MONO : undefined}>
                {children}
            </Text>
        </>
    )
}

const OWNER_LABEL = { name: 'videosPage.infoOwnerName', email: 'videosPage.infoOwnerEmail', uid: 'videosPage.infoOwnerUid' }

// Every field of one video, the sender as a single identity (name, else
// email, else uid — resolved server-side), and a link to all their videos.
export function VideoInfoDialog({ video, eventId, open, onOpenChange }) {
    const { t } = useTranslation()
    // Keyed by video, so a dialog reopened on another video reads as loading
    // until its own answer arrives, with no reset inside the effect.
    const [lookup, setLookup] = useState({ videoId: null, owner: null, failed: false })

    const videoId = open ? video?.id : null
    useEffect(() => {
        if (!videoId) return
        let cancelled = false
        fetchVideoOwner(videoId)
            .then((owner) => { if (!cancelled) setLookup({ videoId, owner, failed: false }) })
            .catch((err) => {
                console.warn('Could not look up the video owner:', err.message)
                if (!cancelled) setLookup({ videoId, owner: null, failed: true })
            })
        return () => { cancelled = true }
    }, [videoId])

    const ownerState = lookup.videoId !== videoId ? 'loading' : lookup.failed ? 'error' : 'done'
    const owner = ownerState === 'done' ? lookup.owner : null

    const close = () => onOpenChange({ open: false })

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange} scrollBehavior="inside">
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content maxW={{ base: '95vw', md: '640px' }}>
                        <Dialog.Header>
                            <Dialog.Title>{t('videosPage.infoTitle')}</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" />
                        </Dialog.CloseTrigger>
                        <Dialog.Body>
                            {video && (
                                <>
                                    <Box mb={5}>
                                        <Text fontSize="sm" color="var(--mc-text-muted)" mb={1}>{t('videosPage.infoOwner')}</Text>
                                        {ownerState === 'loading' && <Spinner size="sm" />}
                                        {ownerState === 'error' && (
                                            <Text fontSize="sm" color="red.400">{t('videosPage.infoOwnerFailed')}</Text>
                                        )}
                                        {ownerState === 'done' && (
                                            <HStack gap={2} wrap="wrap">
                                                <LuUser />
                                                <Text fontSize="sm" color="var(--mc-text-muted)">{t(OWNER_LABEL[owner?.kind] || OWNER_LABEL.uid)}:</Text>
                                                <Text fontSize="sm" color="var(--mc-text)" wordBreak="break-all" fontFamily={owner?.kind === 'uid' ? MONO : undefined}>
                                                    {owner?.value || t('videosPage.infoOwnerUnknown')}
                                                </Text>
                                            </HStack>
                                        )}
                                        {video.uid && (
                                            <Text fontSize="sm" mt={2} textDecoration="underline">
                                                <RouterLink
                                                    to={`/events/${eventId}/videos?uid=${encodeURIComponent(video.uid)}`}
                                                    onClick={close}
                                                >
                                                    {t('videosPage.infoAllByUser')}
                                                </RouterLink>
                                            </Text>
                                        )}
                                    </Box>
                                    <Grid templateColumns="minmax(120px, auto) 1fr" columnGap={4} rowGap={2}>
                                        <Field label={t('videosPage.infoId')} mono>{video.id}</Field>
                                        {videoInfoRows(video).map(({ key, value }) => (
                                            <Field key={key} label={key} mono>{value}</Field>
                                        ))}
                                    </Grid>
                                </>
                            )}
                        </Dialog.Body>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    )
}
